import { UserOpeningResponse } from './../../entities/account/UserResponse';
import { email } from './../../../core/utils/form/validation/validators';
import {
  UserId,
  UserResponse,
  UserSettingsResponse,
  UserSocialId,
  LoginActivityResponse,
  EmailItemResponse,
  AccountResponse,
  CompactUserResponse,
  VeryCompactUserResponse,
  TwoFactorResponse,
  TwoFactorType,
} from '@business/entities/account';
import { CommonService } from '@business/services';
import { Endpoint } from '../endpoint';
import { MetaResponse } from '@business/entities/common';
import { WorkingHourData } from '@modules/account/MyAccountPage/WorkingHours';
import { MySchedularFormData } from '@modules/CalendarPage/EditMyScheduleModal/EditMyScheduleModal';
import { CompanyId } from '@business/entities/companies';
import { memoize } from '@core/utils/rxjs/operators';
import { AppConfig } from '@core/utils/config';

export function fetchUsers(refresh = false) {
  return Endpoint.get<UserResponse[]>(`@gc/users`).pipe(
    memoize({
      key: 'fetchUsers',
      options: { refresh, timeout: 30 * 60 },
      params: { appId: AppConfig.current().appId, companyId: AppConfig.current().companyId },
    }),
  );
}

export function fetchTenantUsers() {
  return Endpoint.get<TenantUserResponse[]>(`@gc/users/tenant-users`);
}

export function fetchBoardUsers() {
  return Endpoint.get<UserResponse[]>(`@gc/users/board-users`);
}

export function fetchBoardExecutives() {
  return Endpoint.get<VeryCompactUserResponse[]>(`@gc/users/board-executives`);
}

export function fetchUser(id: UserId) {
  return Endpoint.get<UserResponse>(`@gc/users/user/${id}`);
}

export function fetchUserTrashed(id: UserId) {
  return Endpoint.get<UserResponse>(`@gc/users/user-trashed/${id}`);
}

export function fetchEmails(id: UserId) {
  return Endpoint.get<EmailItemResponse[]>(`@gc/users/emails/${id}`);
}

export function fetchUserSocials(id: UserId) {
  return Endpoint.get<UserResponse>(`@gc/users/user/${id}`);
}

export function createUser(user: Partial<UserResponse>) {
  return Endpoint.post<UserResponse>(`@gc/users/create`, { data: { ...user } });
}

export function updateUser(userId: UserId, data: Partial<UserResponse>) {
  const formData = CommonService.toFormData(data);

  return Endpoint.post<UserResponse>(`@gc/users/${userId}`, { formData, hasFiles: true });
}

export function updateWorkingHours(data: WorkingHourData) {
  return Endpoint.patch<UserResponse>(`@gc/users/working-hours`, { data });
}

export function updateCalendarSettings(data: MySchedularFormData) {
  return Endpoint.patch<UserResponse>(`@gc/users/calendar-settings`, { data });
}

export function changePassword(userId: UserId, password: string) {
  return Endpoint.patch<UserResponse>(`@gc/users/change-password/${userId}`, { data: { password } });
}

export function deleteUser(userId: UserId, otherUser?: UserId) {
  if (otherUser) {
    return Endpoint.delete<void>(`@gc/users/user/${userId}/${otherUser}`);
  }
  return Endpoint.delete<void>(`@gc/users/${userId}`);
}

export function deleteUserSocial(socialId: UserSocialId) {
  return Endpoint.delete<UserResponse>(`@gc/users/social-account/${socialId}`);
}

export function updateSocialSignature(socialId: UserSocialId, signature: string) {
  return Endpoint.post<UserResponse>(`@gc/user-social/signature/${socialId}`, { data: { signature } });
}

export function updateSettings(data: Partial<UserSettingsResponse>) {
  return Endpoint.patch<UserResponse>(`@gc/users/settings`, { data });
}

export function loginActivity(userId: UserId) {
  return Endpoint.get<LoginActivityResponse[]>(`@gc/users/login-activity/${userId}`);
}

export function getUserAccounts() {
  return Endpoint.get<AccountResponse[]>(`@gc/users/accounts`);
}

export function checkUser(email: string) {
  return Endpoint.get<CompactUserResponse>(`@gc/users/check-users`, { params: { email } });
}

export function setupTwoFactor(phone: string, type: TwoFactorType) {
  return Endpoint.post<TwoFactorResponse>(`@gc/users/setup-two-factor`, { data: { phone, type } });
}

export function verifyTwoFactor(phone: string, code: string, token: string, type: TwoFactorType) {
  return Endpoint.post<void>(`@gc/users/verify-two-factor`, { data: { phone, code, token, type } });
}

export function disableTwoFactor(type: TwoFactorType) {
  return Endpoint.post<void>(`@gc/users/disable-two-factor`, { data: { type } });
}

export function findMeta<T>(key: string) {
  return Endpoint.get<MetaResponse<T>>(`@gc/users/find-meta`, { params: { key } });
}

export function saveMeta(key: string, value: any) {
  return Endpoint.post<void>(`@gc/users/save-meta`, { data: { key, value } });
}

export function deleteMeta(key: string) {
  return Endpoint.delete<void>(`@gc/users/delete-meta`, { params: { key } });
}

export function exchangeToken(authToken: string) {
  return Endpoint.post<{ token: string; appId: string; companyId: CompanyId }>(`@gc/non-tenant/users/exchange-token`, { data: { authToken } });
}

export function libraryExchangeToken(authToken: UserId) {
  return Endpoint.post<{ token: string; appId: string; companyId: CompanyId }>(`@gc/non-tenant/users/library-exchange-token`, {
    data: { authToken },
  });
}

export function savePermissionSettings(key: string, value: any, userId: UserId) {
  return Endpoint.patch<void>(`@gc/users/permission-settings/${userId}`, { data: { key, value, userId } });
}

export function findUserMeta<T>(userId: UserId, key: string) {
  return Endpoint.get<MetaResponse<T>>(`@gc/users/find-user-meta/${userId}`, { params: { key } });
}

export function updateNotifications(notifications: any) {
  return Endpoint.post<UserResponse>(`@gc/users/notifications`, { data: { notifications } });
}

export function updateEmailNotifications(notifications: any) {
  return Endpoint.post<UserResponse>(`@gc/users/email-notifications`, { data: { notifications } });
}
